import React, { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react"; // Import icons

const CustomSlider = ({ cards, cardsToShow = 3, CardComponent, type }) => {
	const [currentSlide, setCurrentSlide] = useState(0);
	const [currentRow1, setCurrentRow1] = useState(0);
	const [currentRow2, setCurrentRow2] = useState(0);
	// Calculate the total number of slides
	const totalSlides = Math.ceil(cards.length / cardsToShow);

	// Handle next slide
	const nextSlide = () => {
		setCurrentSlide((prev) => (prev + 1) % totalSlides);
	};

	// Handle previous slide
	const prevSlide = () => {
		setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
	};

	return (
		<>
			{type === "home" && (
				<div className="relative w-full">
					{/* Slides Container for Home */}
					<div className="overflow-hidden">
						<div
							className="flex transition-transform duration-500 ease-in-out"
							style={{
								transform: `translateX(-${currentSlide * (100 / cardsToShow)}%)`,
							}}
						>
							{cards.map((card, index) => (
								<div
									key={index}
									className="flex-shrink-0 p-1"
									style={{ width: `calc(100% / ${cardsToShow})` }}
								>
									<CardComponent variant="product" content={card} />
								</div>
							))}
						</div>
					</div>

					{/* Navigation Buttons */}
					<button
						onClick={prevSlide}
						className="custom-prev absolute left-[-5%] top-1/2 transform -translate-y-1/2 w-[40px] h-[40px] p-[8px] rounded-[20px] shadow-md bg-[#47645D] flex items-center justify-center z-10"
					>
						<ChevronLeft size={24} className="text-white" />
					</button>
					<button
						onClick={nextSlide}
						className="custom-next absolute right-[-1%] top-1/2 transform -translate-y-1/2 w-[40px] h-[40px] p-[8px] rounded-[20px] shadow-md bg-[#47645D] flex items-center justify-center z-10"
					>
						<ChevronRight size={24} className="text-white" />
					</button>

					{/* Pagination Dots */}
					<div className="mt-4 flex justify-center space-x-2">
						{Array.from({ length: totalSlides }).map((_, index) => (
							<button
								key={index}
								onClick={() => setCurrentSlide(index)}
								className={`w-3 h-3 rounded-full transition-colors ${
									index === currentSlide ? "bg-paginationBg" : "bg-white"
								}`}
							/>
						))}
					</div>
				</div>
			)}

			{/* For 'search' type, create a grid with pagination and no navigation */}
			{type === "search" && (
				<div className="relative w-full flex flex-col items-center">
					<div className="overflow-hidden w-full">
						<div className="grid grid-cols-3 gap-4">
							{cards
								.slice(currentSlide * 6, (currentSlide + 1) * 6) // Show 6 cards per page
								.map((card, index) => (
									<div key={index} className="p-2">
										<CardComponent variant="priceFilter" content={card} />
									</div>
								))}
						</div>
					</div>

					{/* Pagination Controls */}
					<div className="mt-4 flex items-center space-x-2">
						<button
							onClick={prevSlide}
							disabled={currentSlide === 0}
							className="w-[78px] h-[32px] rounded-[24px] p-[8px] bg-[#B7D974] flex items-center justify-center gap-[2px] text-sm font-montserrat font-medium leading-[24px] text-center"
						>
							Previous
						</button>

						{Array.from({ length: Math.ceil(cards.length / 6) }).map(
							(_, index) => (
								<button
									key={index}
									onClick={() => setCurrentSlide(index)}
									className={`w-[40px] h-[40px] rounded-[8px] p-[12px] flex items-center justify-center font-montserrat font-medium text-[13px] leading-[24px] ${
										index === currentSlide
											? "bg-paginationSecondaryBg text-[#193D34]"
											: "bg-[#A6A6A6] text-white"
									}`}
								>
									{index + 1}
								</button>
							)
						)}

						<button
							onClick={nextSlide}
							disabled={currentSlide === Math.ceil(cards.length / 6) - 1}
							className="w-[78px] h-[32px] rounded-[24px] p-[8px] bg-[#B7D974] flex items-center justify-center gap-[2px] text-sm font-montserrat font-medium leading-[24px] text-center"
						>
							Next
						</button>
					</div>
				</div>
			)}
			{/* Mobile view for 'search' type */}
			{type === "searchMobile" && (
				<div className="lg:hidden flex flex-col w-full h-full items-center">
					{/* First Row */}
					<div className="relative w-full mb-8 h-[519px]">
						<div className="overflow-hidden w-full h-full">
							<div
								className="flex h-full transition-transform duration-300"
								style={{ transform: `translateX(-${currentRow1 * 100}%)` }}
							>
								{cards.map((card, index) => (
									<div
										key={`row1-${index}`}
										className="w-full flex-shrink-0 p-2 h-full"
									>
										<div className="relative h-full">
											<CardComponent
												variant="priceFilter"
												content={card}
												className="w-[280px] h-full mx-auto"
											/>
											{/* Navigation for Row 1 */}
											<button
												onClick={() =>
													setCurrentRow1((prev) => Math.max(0, prev - 1))
												}
												disabled={currentRow1 === 0}
												className="custom-prev absolute left-0 top-1/2 -translate-y-1/2 w-[40px] h-[40px] p-[8px] rounded-full shadow-md bg-[#47645D] flex items-center justify-center z-10 transition-opacity duration-300 disabled:opacity-50"
											>
												<ChevronLeft size={20} className="text-white" />
											</button>
											<button
												onClick={() =>
													setCurrentRow1((prev) =>
														Math.min(Math.ceil(cards.length / 1) - 1, prev + 1)
													)
												}
												disabled={
													currentRow1 >= Math.ceil(cards.length / 1) - 1
												}
												className="custom-next absolute right-0 top-1/2 -translate-y-1/2 w-[40px] h-[40px] p-[8px] rounded-full shadow-md bg-[#47645D] flex items-center justify-center z-10 transition-opacity duration-300 disabled:opacity-50"
											>
												<ChevronRight size={20} className="text-white" />
											</button>
										</div>
									</div>
								))}
							</div>
						</div>
					</div>

					{/* Second Row */}
					<div className="relative w-full mb-8 h-[519px]">
						<div className="overflow-hidden w-full h-full">
							<div
								className="flex h-full transition-transform duration-300"
								style={{ transform: `translateX(-${currentRow2 * 100}%)` }}
							>
								{cards.map((card, index) => (
									<div
										key={`row2-${index}`}
										className="w-full flex-shrink-0 p-2 h-full"
									>
										<div className="relative h-full">
											<CardComponent
												variant="priceFilter"
												content={card}
												className="w-[280px] h-full mx-auto"
											/>
											{/* Navigation for Row 2 */}
											<button
												onClick={() =>
													setCurrentRow2((prev) => Math.max(0, prev - 1))
												}
												disabled={currentRow2 === 0}
												className="custom-prev absolute left-0 top-1/2 -translate-y-1/2 w-[40px] h-[40px] p-[8px] rounded-full shadow-md bg-[#47645D] flex items-center justify-center z-10 transition-opacity duration-300 disabled:opacity-50"
											>
												<ChevronLeft size={20} className="text-white" />
											</button>
											<button
												onClick={() =>
													setCurrentRow2((prev) =>
														Math.min(Math.ceil(cards.length / 1) - 1, prev + 1)
													)
												}
												disabled={
													currentRow2 >= Math.ceil(cards.length / 1) - 1
												}
												className="custom-next absolute right-0 top-1/2 -translate-y-1/2 w-[40px] h-[40px] p-[8px] rounded-full shadow-md bg-[#47645D] flex items-center justify-center z-10 transition-opacity duration-300 disabled:opacity-50"
											>
												<ChevronRight size={20} className="text-white" />
											</button>
										</div>
									</div>
								))}
							</div>
						</div>
					</div>

					{/* Combined Pagination */}
					<div className="mt-4 flex items-center space-x-5 pt-24">
						<button
							onClick={() => {
								const newPage = Math.max(
									0,
									Math.min(currentRow1, currentRow2) - 1
								);
								setCurrentRow1(newPage);
								setCurrentRow2(newPage);
							}}
							disabled={currentRow1 === 0 && currentRow2 === 0}
							className="w-[78px] h-[32px] rounded-[24px] p-[8px] bg-[#B7D974] flex items-center justify-center gap-[2px] text-sm font-montserrat font-medium leading-[24px] text-center"
						>
							Previous
						</button>

						{/* Show only current page and adjacent pages */}
						{Array.from({
							length: Math.min(3, Math.ceil(cards.length / 1)),
						}).map((_, index) => {
							// Calculate page number to display (current page centered when possible)
							let pageToShow;
							const currentPage = Math.max(currentRow1, currentRow2);
							const totalPages = Math.ceil(cards.length / 1);

							if (totalPages <= 3) {
								pageToShow = index;
							} else if (currentPage === 0) {
								pageToShow = index;
							} else if (currentPage === totalPages - 1) {
								pageToShow = totalPages - 3 + index;
							} else {
								pageToShow = currentPage - 1 + index;
							}

							if (pageToShow >= totalPages) return null;

							return (
								<button
									key={pageToShow}
									onClick={() => {
										setCurrentRow1(pageToShow);
										setCurrentRow2(pageToShow);
									}}
									className={`w-[40px] h-[40px] rounded-[8px] p-[12px] flex items-center justify-center font-montserrat font-medium text-[13px] leading-[24px] ${
										pageToShow === currentRow1 || pageToShow === currentRow2
											? "bg-paginationSecondaryBg text-[#193D34]"
											: "bg-[#A6A6A6] text-white"
									}`}
								>
									{pageToShow + 1}
								</button>
							);
						})}

						<button
							onClick={() => {
								const newPage = Math.min(
									Math.ceil(cards.length / 1) - 1,
									Math.max(currentRow1, currentRow2) + 1
								);
								setCurrentRow1(newPage);
								setCurrentRow2(newPage);
							}}
							disabled={
								currentRow1 >= Math.ceil(cards.length / 1) - 1 &&
								currentRow2 >= Math.ceil(cards.length / 1) - 1
							}
							className="w-[78px] h-[32px] rounded-[24px] p-[8px] bg-[#B7D974] flex items-center justify-center gap-[2px] text-sm font-montserrat font-medium leading-[24px] text-center"
						>
							Next
						</button>
					</div>
				</div>
			)}
			{/* New "trend" variant (only navigation buttons) */}
			{type === "trend" && (
				<div className="relative w-full">
					{/* Slides Container for Trend */}
					<div className="overflow-hidden">
						<div
							className="flex transition-transform duration-500 ease-in-out"
							style={{
								transform: `translateX(-${currentSlide * (100 / cardsToShow)}%)`,
							}}
						>
							{cards.map((card, index) => (
								<div
									key={index}
									className="flex-shrink-0 p-1"
									style={{ width: `calc(100% / ${cardsToShow})` }}
								>
									<CardComponent variant="amazonTopSellers" content={card} />
								</div>
							))}
						</div>
					</div>

					{/* Navigation Buttons (Only) */}
					<button
						onClick={prevSlide}
						className="custom-prev absolute left-[-5%] top-1/2 transform -translate-y-1/2 w-[40px] h-[40px] p-[8px] rounded-[20px] shadow-md bg-sliderNavBg flex items-center justify-center z-10"
					>
						<ChevronLeft size={24} className="text-black" />
					</button>
					<button
						onClick={nextSlide}
						className="custom-next absolute right-[-1%] top-1/2 transform -translate-y-1/2 w-[40px] h-[40px] p-[8px] rounded-[20px] shadow-md bg-sliderNavBg flex items-center justify-center z-10"
					>
						<ChevronRight size={24} className="text-black" />
					</button>
				</div>
			)}
			{/* For trendSimilarProducts */}
			{type === "trendSimilarProducts" && (
				<div className="relative w-full flex flex-col items-center">
					<div className="overflow-hidden w-full">
						<div className="grid grid-cols-6 gap-1">
							{cards
								.slice(currentSlide * 6, (currentSlide + 1) * 6) // Show 6 cards per page
								.map((card, index) => (
									<div key={index} className="p-2">
										<CardComponent
											variant="trendSimilarProducts"
											content={card}
										/>
									</div>
								))}
						</div>
					</div>

					{/* Chevron Navigation Buttons */}
					<button
						onClick={prevSlide}
						disabled={currentSlide === 0}
						className="custom-prev absolute left-[-5%] top-[40%] transform -translate-y-1/2 w-[40px] h-[40px] p-[8px] rounded-[20px] shadow-md bg-sliderNavBg flex items-center justify-center z-10 disabled:opacity-50"
					>
						<ChevronLeft size={24} className="text-black" />
					</button>
					<button
						onClick={nextSlide}
						disabled={currentSlide === Math.ceil(cards.length / 6) - 1}
						className="custom-next absolute right-[-2%] top-[40%] transform -translate-y-1/2 w-[40px] h-[40px] p-[8px] rounded-[20px] shadow-md bg-sliderNavBg flex items-center justify-center z-10 disabled:opacity-50"
					>
						<ChevronRight size={24} className="text-black" />
					</button>

					{/* Pagination Controls */}
					<div className="mt-4 flex items-center space-x-2">
						<button
							onClick={prevSlide}
							disabled={currentSlide === 0}
							className="w-[78px] h-[32px] rounded-[24px] p-[8px] bg-[#B7D974] flex items-center justify-center gap-[2px] text-sm font-montserrat font-medium leading-[24px] text-center"
						>
							Previous
						</button>

						{Array.from({ length: Math.ceil(cards.length / 6) }).map(
							(_, index) => (
								<button
									key={index}
									onClick={() => setCurrentSlide(index)}
									className={`w-[40px] h-[40px] rounded-[8px] p-[12px] flex items-center justify-center font-montserrat font-medium text-[13px] leading-[24px] ${
										index === currentSlide
											? "bg-paginationSecondaryBg text-[#193D34]"
											: "bg-white text-[#A6A6A6]"
									}`}
								>
									{index + 1}
								</button>
							)
						)}

						<button
							onClick={nextSlide}
							disabled={currentSlide === Math.ceil(cards.length / 6) - 1}
							className="w-[78px] h-[32px] rounded-[24px] p-[8px] bg-[#B7D974] flex items-center justify-center gap-[2px] text-sm font-montserrat font-medium leading-[24px] text-center"
						>
							Next
						</button>
					</div>
				</div>
			)}
		</>
	);
};

export default CustomSlider;
